import { useState } from "react";
import { uploadMedia } from "../../../../utils/apiService";

interface UseUploadReturn {
  uploadedFile: File | null;
  isUploading: boolean;
  error: string | null;
  handleFileUpload: (file: File) => void;
  handleError: (error: string) => void;
  resetUpload: () => void;
}

export const useUpload = (): UseUploadReturn => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const getFileType = (file: File): string => {
    if (file.type.startsWith("image/")) {
      return "image";
    } else if (file.type.startsWith("video/")) {
      return "video";
    }
    return "file";
  };

  const handleFileUpload = async (file: File) => {
    setError(null);
    setUploadedFile(null);
    setIsUploading(true);

    try {
      // Convert file to base64
      const base64Data = await convertFileToBase64(file);
      const type = getFileType(file);

      // Log the base64 data for debugging
      console.log("Base64 data:", base64Data);
      console.log("File type:", type);
      console.log("File name:", file.name);
      console.log("File size:", file.size);

      // For files uploaded from computer, don't include URL
      const url = "";

      // Upload to API
      await uploadMedia(base64Data, type, url);

      setUploadedFile(file);
      setIsUploading(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(error instanceof Error ? error.message : "שגיאה בהעלאת הקובץ");
      setIsUploading(false);
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setIsUploading(false);
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setIsUploading(false);
    setError(null);
  };

  return {
    uploadedFile,
    isUploading,
    error,
    handleFileUpload,
    handleError,
    resetUpload,
  };
};
